/* eslint-disable */
/**
 * Shared DE (Decision Engine) enforcement gate — used by both Rental (AgreementDetails.tsx)
 * and Customer Order (CustomerOrderAgrDetails.tsx) to run the enforcement check, fire GA
 * bypass/hard-block tags, and render the resulting popup with a single implementation.
 */
import React, { useState } from 'react';
import { Grid, Typography, RACButton, RACModalCard } from '@rentacenter/racstrap';
import { useTranslation } from 'react-i18next';
import {
  evaluateDEEnforcement,
  formatExceedPercent,
  buildDEEnforcementTagEvent,
  DE_BYPASS_TAG_REASONS,
  resolveDEApprovalDetails,
} from '../../utils/deEnforcementUtils';
import { DEEnforcementEvaluation, DEEnforcementConfig } from '../../types/types';
import { storeException } from '../../api/user';
import { DE_ENFORCEMENT_EXCEPTION_REF_CODE } from '../../constants/constants';
import SecondFactor from '../Rental/TwoFactorPopup/TwoFactorPopup';

export interface UseDEEnforcementGateParams {
  deEnforcementConfig: DEEnforcementConfig;
  getCustomerResponse: any;
  deEnforcementBaseline: { agreementRate?: any; sacTotal?: any } | null | undefined;
  agreementType?: string;
  isSwitchOut?: boolean;
  // GA tag context — same shape as buildDEEnforcementTagEvent's shared identity fields.
  agreementNo: any;
  customerId: any;
  storeNo: any;
  employeeId?: any;
  role: any;
  currentScreen: string;
}

export function useDEEnforcementGate(params: UseDEEnforcementGateParams) {
  const [deEnforcementPopup, setDeEnforcementPopup] = useState<{
    open: boolean;
    evaluation: DEEnforcementEvaluation | null;
  }>({ open: false, evaluation: null });
  const [deOverrideAuthOpen, setDeOverrideAuthOpen] = useState(false);

  const runDEEnforcementCheck = (): DEEnforcementEvaluation => {
    const resolved = resolveDEApprovalDetails({
      getCustomerResponse: params.getCustomerResponse,
    });
    return evaluateDEEnforcement({
      config: params.deEnforcementConfig,
      ...resolved,
      weeklyRate: params.deEnforcementBaseline?.agreementRate,
      totalCashPrice: params.deEnforcementBaseline?.sacTotal,
      agreementType: params.agreementType,
      isSwitchOut: params.isSwitchOut,
    });
  };

  // Runs the DE check, fires bypass/hard-block GA tags, and opens the popup when an
  // override is required or the agreement is blocked. Returns true when the caller
  // should proceed immediately (PASS or an untagged bypass).
  const evaluateAndGate = (): boolean => {
    const evaluation = runDEEnforcementCheck();
    const tagContext = {
      verifyLevel: evaluation.verifyLevel,
      agreementNo: params.agreementNo,
      customerId: params.customerId,
      storeNo: params.storeNo,
      employeeId: params.employeeId,
      role: params.role,
      currentScreen: params.currentScreen,
    };
    if (evaluation.status === 'BYPASS' && DE_BYPASS_TAG_REASONS.includes(evaluation.reason || '')) {
      window?.dataLayer?.push(buildDEEnforcementTagEvent({
        tagType: 'BYPASS',
        bypassReason: evaluation.reason,
        ...tagContext,
      }));
    }
    if (evaluation.status === 'HARD_BLOCK') {
      window?.dataLayer?.push(buildDEEnforcementTagEvent({
        tagType: 'ENFORCED_HARD_BLOCK',
        exceedPercent: evaluation.exceedPercent,
        ...tagContext,
      }));
    }
    if (evaluation.status === 'HARD_BLOCK' || evaluation.status === 'APPROVAL_REQUIRED') {
      setDeEnforcementPopup({ open: true, evaluation });
      return false;
    }
    return true;
  };

  const closeDEEnforcementPopup = () => setDeEnforcementPopup({ open: false, evaluation: null });
  const authorizeDEEnforcementOverride = () => {
    setDeEnforcementPopup((prev) => ({ ...prev, open: false }));
    setDeOverrideAuthOpen(true);
  };

  // Logs the store exception + fires the ENFORCED_OVERRIDE GA tag after agreement creation
  // succeeds with a DE override in effect. Called with the just-created agreement's ID since
  // the hook's own agreementNo reflects the pre-creation value.
  const recordDEOverrideAudit = async (auditParams: {
    agreementId: any;
    approverDetails: { approverId: any; approverRole: any; authenticationMethod: any };
  }) => {
    const { agreementId, approverDetails } = auditParams;
    const enforcedAgreementRate = params.deEnforcementBaseline?.agreementRate;
    const enforcedSacTotal = params.deEnforcementBaseline?.sacTotal;
    const deOverrideStoreExceptionPayload = {
      storeException: [{
        identifierId: Number(agreementId),
        identifierType: 'AGREEMENT',
        storeNumber: sessionStorage.getItem('storeNumber'),
        exceptionTypeRefCode: DE_ENFORCEMENT_EXCEPTION_REF_CODE,
        exceptionText: `DE Approval Amount Override by ${approverDetails.approverRole} (${approverDetails.authenticationMethod}) - Enforced Agreement Rate: $${enforcedAgreementRate}, SAC Total: $${enforcedSacTotal}`,
        approverId: approverDetails.approverId != null ? String(approverDetails.approverId) : approverDetails.approverId,
      }],
    };
    await storeException(deOverrideStoreExceptionPayload);
    window?.dataLayer?.push(buildDEEnforcementTagEvent({
      tagType: 'ENFORCED_OVERRIDE',
      overrideTier: deEnforcementPopup.evaluation?.tier,
      overridePercentage: deEnforcementPopup.evaluation?.tier
        ? params.deEnforcementConfig.levelConfigs[String(deEnforcementPopup.evaluation?.verifyLevel)]?.[deEnforcementPopup.evaluation.tier]
        : null,
      exceedPercent: deEnforcementPopup.evaluation?.exceedPercent,
      verifyLevel: deEnforcementPopup.evaluation?.verifyLevel,
      approverId: approverDetails.approverId,
      approverRole: approverDetails.approverRole,
      authenticationMethod: approverDetails.authenticationMethod,
      agreementNo: agreementId,
      customerId: params.customerId,
      storeNo: params.storeNo,
      employeeId: params.employeeId,
      role: params.role,
      currentScreen: params.currentScreen,
    }));
  };

  return {
    deEnforcementPopup,
    deOverrideAuthOpen,
    setDeOverrideAuthOpen,
    evaluateAndGate,
    closeDEEnforcementPopup,
    authorizeDEEnforcementOverride,
    recordDEOverrideAudit,
  };
}

export interface DEEnforcementModalProps {
  isOpen: boolean;
  evaluation: DEEnforcementEvaluation | null;
  onClose: () => void;
  onAuthorize: () => void;
  classes: any;
  hardBlockMessage?: string;
}

export function DEEnforcementModal({
  isOpen,
  evaluation,
  onClose,
  onAuthorize,
  classes,
  hardBlockMessage,
}: DEEnforcementModalProps) {
  const { t } = useTranslation('rental');
  if (!isOpen || !evaluation) return null;

  const content = evaluation.status === 'HARD_BLOCK' ? (
    <Grid style={{ padding: '10px 24px 24px 24px' }}>
      <Grid className={classes.textCenter} style={{ marginBottom: '16px' }}>
        <Typography style={{ fontFamily: 'OpenSans-bold' }}>
          {t(hardBlockMessage || 'This agreement exceeds the maximum override authority and cannot be submitted.')}
        </Typography>
      </Grid>
      <Grid className={classes.textCenter}>
        <RACButton variant="contained" color="primary" onClick={() => onClose()}>
          {t('OK')}
        </RACButton>
      </Grid>
    </Grid>
  ) : (
    <Grid style={{ padding: '10px 24px 24px 24px' }}>
      <Grid className={classes.textCenter} style={{ marginBottom: '16px' }}>
        <Typography style={{ fontFamily: 'OpenSans-bold' }}>
          {t('This agreement exceeds the Approved amount by')} {formatExceedPercent(evaluation.exceedPercent)}%{' '}
          {t('and requires')} {evaluation.tier} {t('approval to continue.')}
        </Typography>
      </Grid>
      <Grid className={classes.textCenter}>
        <RACButton className={classes.mx1} variant="outlined" color="primary" onClick={() => onClose()}>
          {t('Cancel')}
        </RACButton>
        <RACButton className={classes.mx1} variant="contained" color="primary" onClick={() => onAuthorize()}>
          {t('Authorize')}
        </RACButton>
      </Grid>
    </Grid>
  );

  return (
    <RACModalCard
      isOpen={isOpen}
      closeIcon={false}
      borderRadius={'25px !important'}
      onClose={onClose}
      maxWidth={'xs'}
      //eslint-disable-next-line react/no-children-prop
      children={content}
    />
  );
}

export interface DEOverrideAuthGateProps {
  deEnforcementPopup: { open: boolean; evaluation: DEEnforcementEvaluation | null };
  deOverrideAuthOpen: boolean;
  setDeOverrideAuthOpen: (open: boolean) => void;
  closeDEEnforcementPopup: () => void;
  authorizeDEEnforcementOverride: () => void;
  // Caller-specific action to run once the override authentication succeeds
  // (e.g. Rental's ConfirmDueDateAction, CO's setsecondFactorEnabled(true)).
  onOverrideAuthenticated: () => void;
  setApproverDetails: (details: any) => void;
  currentRole: string;
  classes: any;
  hardBlockMessage?: string;
}

// Combines the DEEnforcementModal (evaluation popup) and the override-authentication
// SecondFactor into one render, since every caller wires them together identically.
export function DEOverrideAuthGate({
  deEnforcementPopup,
  deOverrideAuthOpen,
  setDeOverrideAuthOpen,
  closeDEEnforcementPopup,
  authorizeDEEnforcementOverride,
  onOverrideAuthenticated,
  setApproverDetails,
  currentRole,
  classes,
  hardBlockMessage,
}: DEOverrideAuthGateProps) {
  return (
    <>
      {deEnforcementPopup.open ? (
        <DEEnforcementModal
          isOpen={deEnforcementPopup.open}
          evaluation={deEnforcementPopup.evaluation}
          onClose={closeDEEnforcementPopup}
          onAuthorize={authorizeDEEnforcementOverride}
          classes={classes}
          hardBlockMessage={hardBlockMessage}
        />
      ) : null}
      {deOverrideAuthOpen ? (
        <SecondFactor
          setTwoFactorCancelClick={() => setDeOverrideAuthOpen(false)}
          setTwoFactorCompleted={(value: any) => {
            if (value) {
              setDeOverrideAuthOpen(false);
              onOverrideAuthenticated();
            }
          }}
          setApproverDetails={(details: any) => setApproverDetails(details)}
          moduleName={'DEEnforcement'}
          minRole={deEnforcementPopup.evaluation?.tier}
          dynamicRoleAuth={true}
          currentRole={currentRole}
          throwCurrentPage={'ItemSearch'}
          closeSLAClicked={() => setDeOverrideAuthOpen(false)}
        />
      ) : null}
    </>
  );
}
